// src/pages/Community.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../context/AuthContext";
import { fetchClubs, fetchMyClubs } from "../services/clubsApi";
import UnifiedPostComposer from "../features/posts/UnifiedPostComposer";
import { normalizeForumTagsForThread } from "../features/posts/forumFeedTagUtils";
import {
  getV2CreatePostId,
  mapV2CreateResultToThreadNavState,
} from "../features/posts/mapV2CreateResultToThreadNavState";
import {
  navigateToCanonicalForumThread,
  THREAD_NAV_ORIGIN_COMMUNITY,
  getCanonicalForumThreadAbsoluteUrl,
} from "../features/posts/canonicalThreadNavigation";
import CommunityStartThreadBar from "../components/CommunityStartThreadBar";
import FeedPostCard, { pickViewerId } from "../components/Clubs/FeedPostCard";
import { forumFeedRowToFeedPostCard } from "../features/posts/forumFeedRowToFeedPostCard";
import HotThreadsPanel from "../components/HotThreadsPanel";
import CommunityTopBar from "../components/CommunityTopBar";

// Watch parties rail
import WatchPartyRail from "../components/WatchPartyRail";
import CommunityClubsBar from "../components/CommunityClubsBar";

export default function Community() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // modes: For You / Mine
  const [activeMode, setActiveMode] = useState("foryou");

  // Search
  const [q, setQ] = useState("");

  // Top bar controls
  const [topicFilter, setTopicFilter] = useState("all");
  const [sortBy, setSortBy] = useState("trending");

  const [isComposerOpen, setIsComposerOpen] = useState(false);

  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [postsError, setPostsError] = useState("");
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [shareFeedback, setShareFeedback] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [clubPickerOptions, setClubPickerOptions] = useState([]);

  // Watch parties for the rail
  const [watchPartiesRaw, setWatchPartiesRaw] = useState([]);

  // -------------------------
  // Minimal layout tokens
  // -------------------------
  const ui = useMemo(
    () => ({

      feedScrollOuter: "flex-1 min-h-0 rounded-2xl overflow-hidden",
feedScrollInner: "h-full overflow-y-auto scrollbar-hide",

      // Make the page/frame fill the viewport so the feed can scroll inside
      page: "w-full h-[100svh] px-4 sm:px-6 lg:px-8",
      frame: "w-full mx-auto max-w-[1180px] py-4 sm:py-6 h-full flex flex-col min-h-0",

      // Header row: TopBar (left) + WatchPartyRail (right)
      headerRow:
        "mt-2 grid grid-cols-1 lg:grid-cols-[1fr_0.82fr] gap-4 lg:gap-6 items-start shrink-0",

      // Main grid fills remaining height
      grid:
        "mt-5 grid grid-cols-1 lg:grid-cols-[240px_1fr_240px] gap-5 lg:gap-8 flex-1 min-h-0",

      col: "min-w-0 min-h-0",
      feed: "mt-4 space-y-4 sm:space-y-5",

      // Invisible scroll wrapper (no padding, no bg, no border, no glow)
      // - hides scrollbar via .scrollbar-hide
      // - prevents page from growing; scroll stays inside this area
      feedScroll: "flex-1 min-h-0 overflow-y-auto scrollbar-hide",

      errorText: "text-sm text-red-200",
      infoText: "text-sm text-white/70",

      mobileCtaWrap:
        "lg:hidden fixed left-0 right-0 bottom-0 z-30 pointer-events-none",
      mobileCtaInner:
        "pointer-events-auto px-4 sm:px-6 pb-[max(env(safe-area-inset-bottom),16px)]",
      mobileCtaBtn:
        "w-full h-12 rounded-full border border-white/10 bg-black/70 backdrop-blur-md text-[11px] tracking-[0.22em] uppercase text-emerald-200 hover:bg-black/60 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/25",
    }),
    []
  );

  // -------------------------
  // Fetch watch parties for the rail (poll)
  // -------------------------
  useEffect(() => {
    let alive = true;

    const fetchWatchParties = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_BASE || "/api";
        const res = await fetch(`${API_BASE}/watch-parties`, {
          method: "GET",
          credentials: "include",
          cache: "no-store",
          headers: { Accept: "application/json" },
        });

        if (!res.ok) throw new Error(`watch-parties HTTP ${res.status}`);

        const data = await res.json().catch(() => []);
        if (!alive) return;

        setWatchPartiesRaw(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Failed to fetch watch parties for rail:", e);
        if (alive) setWatchPartiesRaw([]);
      }
    };

    fetchWatchParties();
    const t = setInterval(fetchWatchParties, 10000);

    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  // Map backend -> rail items
  const watchPartyRailItems = useMemo(() => {
    const raw = Array.isArray(watchPartiesRaw) ? watchPartiesRaw : [];

    return raw
      .filter((p) => p.status !== "ended")
      .filter((p) => p.status === "live" || p.status === "waiting")
      .slice(0, 10)
      .map((p) => ({
        id: p.id,
        title: p.title,
        hostName: p.host_username,
        cover: p.cover_url || p.anime_cover || p.host_avatar || null,
        platform: p.platform,
        participantsCount: p.participants_count,
        maxCapacity: p.max_capacity,
        status: p.status,
      }));
  }, [watchPartiesRaw]);

  const handleJoinWatchParty = useCallback(
    (wp) => {
      if (!wp?.id) return;
      navigate(`/watch-party/${wp.id}`);
    },
    [navigate]
  );

  const handleOpenWatchParties = useCallback(() => {
    navigate("/watch-parties");
  }, [navigate]);

  const fetchLikedPosts = async (postIds) => {
    if (!user || !postIds || postIds.length === 0) return;

    const API_BASE = process.env.REACT_APP_API_BASE || "/api";
    const liked = new Set();

    try {
      await Promise.all(
        postIds.map(async (postId) => {
          try {
            const res = await fetch(`${API_BASE}/forum-likes/post/${postId}/check`, {
              credentials: "include",
            });
            if (res.ok) {
              const data = await res.json();
              if (data?.liked) liked.add(postId);
            }
          } catch (err) {
            console.error(`Failed to check like status for post ${postId}:`, err);
          }
        })
      );
      setLikedPosts(liked);
    } catch (err) {
      console.error("Error fetching liked posts:", err);
    }
  };

  const fetchPosts = useCallback(async () => {
    setPostsError("");
    setLoadingPosts(true);

    try {
      const API_BASE = process.env.REACT_APP_API_BASE || "/api";

      const res = await fetch(`${API_BASE}/forum`, {
        method: "GET",
        credentials: "include",
        cache: "no-store",
        headers: { Accept: "application/json" },
      });

      if (!res.ok) {
        let msg = `Failed to load threads (HTTP ${res.status}).`;
        try {
          const err = await res.json();
          msg = err?.message || err?.error || msg;
        } catch {
          try {
            const text = await res.text();
            if (text && text.length < 160) msg = text;
          } catch {}
        }
        throw new Error(msg);
      }

      let data = [];
      try {
        data = await res.json();
      } catch {
        data = [];
      }

      const normalized = Array.isArray(data) ? data.map(normalizeForumRowToThread) : [];
      setPosts(normalized);

      if (user && normalized.length > 0) {
        fetchLikedPosts(normalized.map((p) => p.id));
      }
    } catch (err) {
      setPostsError(err?.message || "Failed to load threads.");
      setPosts([]);
    } finally {
      setLoadingPosts(false);
    }
  }, [user]);

  useEffect(() => {
    let alive = true;

    fetchClubs({ page: 1, pageSize: 4 })
      .then((data) => {
        if (!alive) return;
        setClubs(data?.items ?? []);
      })
      .catch(() => {
        if (alive) setClubs([]);
      });

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setClubPickerOptions([]);
      return;
    }
    let alive = true;
    fetchMyClubs()
      .then((body) => {
        if (!alive) return;
        const rows = Array.isArray(body?.clubs) ? body.clubs : [];
        setClubPickerOptions(
          rows
            .map((c) => ({
              id: Number(c.club_id ?? c.id),
              name: String(c.name || "").trim() || "Club",
              slug: c.slug,
            }))
            .filter((c) => Number.isFinite(c.id) && c.id > 0)
        );
      })
      .catch(() => {
        if (alive) setClubPickerOptions([]);
      });
    return () => {
      alive = false;
    };
  }, [user]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
    const onFocus = () => fetchPosts();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchPosts]);

  const goToThread = (post) => {
    navigateToCanonicalForumThread(navigate, post, { origin: THREAD_NAV_ORIGIN_COMMUNITY });
  };

  const handleReply = (post) => {
    if (!user) {
      setPostsError("Please sign in to reply to posts.");
      return;
    }
    goToThread(post);
  };

  const handleDelete = async (post) => {
    if (!user) {
      setPostsError("Please log in to delete posts.");
      return;
    }

    const API_BASE = process.env.REACT_APP_API_BASE || "/api";

    try {
      const res = await fetch(`${API_BASE}/forum/${post.id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (res.ok) {
        setPosts((prev) => prev.filter((p) => p.id !== post.id));
      } else {
        const err = await res.json().catch(() => ({}));
        setPostsError(err?.error || "Failed to delete post.");
      }
    } catch (err) {
      console.error("Error deleting post:", err);
      setPostsError("Failed to delete post.");
    }
  };

  const handleEdit = (post) => {
    goToThread(post);
  };

  const handleFeedShare = useCallback(async (post) => {
    const url = getCanonicalForumThreadAbsoluteUrl(post);
    if (!url) return;
    const title = post?.title || "Thread";
    const text = `Check out this thread: ${title}`;
    let shareSuccessful = false;

    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        shareSuccessful = true;
      } catch (err) {
        if (err?.name !== "AbortError") {
          console.error("Error sharing:", err);
        }
        return;
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        alert("Link copied to clipboard!");
        shareSuccessful = true;
      } catch (err) {
        console.error("Failed to copy:", err);
        prompt("Copy this link:", url);
        shareSuccessful = true;
      }
    }

    if (shareSuccessful && post?.id) {
      try {
        const API_BASE = process.env.REACT_APP_API_BASE || "/api";
        await fetch(`${API_BASE}/forum/${post.id}/share`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ platform: "copy_link" }),
        });
      } catch (err) {
        console.error("Failed to track share:", err);
      }
    }

    setShareFeedback({ postId: post.id, text: shareSuccessful ? "Shared" : "" });
    window.setTimeout(() => setShareFeedback(null), 2200);
  }, []);

  const handleLike = async (post) => {
    if (!user) {
      setPostsError("Please log in to like posts.");
      return;
    }

    const API_BASE = process.env.REACT_APP_API_BASE || "/api";
    const isLiked = likedPosts.has(post.id);

    try {
      if (isLiked) {
        const res = await fetch(`${API_BASE}/forum-likes/post/${post.id}`, {
          method: "DELETE",
          credentials: "include",
        });

        if (res.ok) {
          setLikedPosts((prev) => new Set([...prev].filter((id) => id !== post.id)));
          setPosts((prevPosts) =>
            prevPosts.map((p) => {
              if (p.id !== post.id) return p;
              const current = Number.isFinite(p.likes)
                ? p.likes
                : parseInt(p.likes || 0, 10);
              return { ...p, likes: Math.max(0, current - 1) };
            })
          );
        }
      } else {
        const res = await fetch(`${API_BASE}/forum-likes`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ post_id: post.id }),
        });

        if (res.ok) {
          setLikedPosts((prev) => new Set([...prev, post.id]));
          setPosts((prevPosts) =>
            prevPosts.map((p) => {
              if (p.id !== post.id) return p;
              const current = Number.isFinite(p.likes)
                ? p.likes
                : parseInt(p.likes || 0, 10);
              return { ...p, likes: current + 1 };
            })
          );
        }
      }
    } catch (err) {
      console.error("Error toggling like:", err);
      setPostsError("Failed to update like.");
    }
  };

  const onClickPost = (e) => {
    e?.preventDefault?.();

    if (!user) {
      setPostsError("Please log in to post a thread.");
      return;
    }

    setPostsError("");
    setIsComposerOpen(true);
  };

  const handleUnifiedComposerSuccess = useCallback(
    async (result) => {
      setPostsError("");
      const postId = getV2CreatePostId(result);
      if (postId != null) {
        const thread = mapV2CreateResultToThreadNavState(result, user);
        if (thread) {
          navigateToCanonicalForumThread(navigate, thread, {
            origin: THREAD_NAV_ORIGIN_COMMUNITY,
          });
        } else {
          const p = result?.post && typeof result.post === "object" ? result.post : null;
          navigateToCanonicalForumThread(
            navigate,
            p != null
              ? {
                  id: String(postId),
                  post_id: postId,
                  club_id: p.club_id,
                  club_slug: p.club_slug,
                }
              : { id: String(postId), post_id: postId },
            { origin: THREAD_NAV_ORIGIN_COMMUNITY }
          );
        }
      }
      await fetchPosts();
    },
    [fetchPosts, navigate, user]
  );

  const filteredPosts = useMemo(() => {
    const query = (q || "").trim().toLowerCase();
    let list = Array.isArray(posts) ? posts : [];

    // Mode filter
    if (activeMode === "mine") {
      const me = getDisplayName(user);
      list = list.filter((p) => p.author === me);
    } else if (activeMode === "foryou") {
      const liked = likedPosts;

      if (user && liked && liked.size > 0) {
        const likedTopicCounts = new Map();
        const likedTagCounts = new Map();

        for (const p of posts) {
          if (!liked.has(p.id)) continue;
          if (p.topicId) {
            likedTopicCounts.set(
              p.topicId,
              (likedTopicCounts.get(p.topicId) || 0) + 1
            );
          }
          for (const t of safeArr(p.tags)) {
            const key = String(t).toLowerCase();
            likedTagCounts.set(key, (likedTagCounts.get(key) || 0) + 1);
          }
        }

        const scoreForYou = (p) => {
          let s = 0;
          if (p.topicId && likedTopicCounts.has(p.topicId)) {
            s += 50 * likedTopicCounts.get(p.topicId);
          }
          for (const t of safeArr(p.tags)) {
            const key = String(t).toLowerCase();
            if (likedTagCounts.has(key)) s += 10 * likedTagCounts.get(key);
          }
          s += (Number(p.likes) || 0) * 2 + (Number(p.replies) || 0) * 3;
          s += Math.max(0, 30 - daysSince(p.createdAt));
          return s;
        };

        list = [...list].sort((a, b) => scoreForYou(b) - scoreForYou(a));
      } else {
        list = [...list].sort((a, b) => scoreTrending(b) - scoreTrending(a));
      }
    } else {
      list = [...list].sort((a, b) => scoreTrending(b) - scoreTrending(a));
    }

    // Topic filter
    if (topicFilter !== "all") {
      list = list.filter((p) => String(p.topicId || "").toLowerCase() === topicFilter);
    }

    // Sort override
    if (sortBy === "new") {
      list = [...list].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
    } else if (sortBy === "top") {
      list = [...list].sort((a, b) => (Number(b.likes) || 0) - (Number(a.likes) || 0));
    } else {
      list = [...list].sort((a, b) => scoreTrending(b) - scoreTrending(a));
    }

    // Search filter
    if (query) {
      list = list.filter((p) => {
        const hay = [p.title, p.body, p.author, p.topicId, safeArr(p.tags).join(" ")]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(query);
      });
    }

    return list;
  }, [posts, activeMode, user, q, likedPosts, topicFilter, sortBy]);

  // lightweight “info” for the top bar
  const onlineFriendsCount = 0;
  const activeNowCount = Math.min(99, (posts?.length || 0) + 7);
  const trendingTopic =
    (() => {
      const counts = new Map();
      for (const p of posts) {
        const key = String(p.topicId || "other").toLowerCase();
        counts.set(key, (counts.get(key) || 0) + 1);
      }
      let best = "arthouse";
      let bestN = -1;
      for (const [k, n] of counts.entries()) {
        if (n > bestN) {
          bestN = n;
          best = k;
        }
      }
      const map = {
        arthouse: "ArtHouse",
        reviewtalk: "ReviewTalk",
        upload: "Upload",
        other: "Other",
      };
      return map[best] || "Community";
    })();

  const viewerIdNum = pickViewerId(user);

  return (
    <div className={ui.page}>
      <div className={ui.frame}>
        {/* HEADER ROW: CommunityTopBar (left) + WatchPartyRail (right) */}
        <div className={ui.headerRow}>
          {/* Left/top area */}
          <div className="min-w-0">
            <CommunityTopBar
              user={user}
              q={q}
              setQ={setQ}
              activeMode={activeMode}
              setActiveMode={setActiveMode}
              sortBy={sortBy}
              setSortBy={setSortBy}
              topicFilter={topicFilter}
              setTopicFilter={setTopicFilter}
              onlineFriendsCount={onlineFriendsCount}
              activeNowCount={activeNowCount}
              trendingTopic={trendingTopic}
            />
          </div>

          {/* Right/top area (≈45% width on desktop via grid ratio) */}
          <div className="min-w-0 lg:pt-7">
            <WatchPartyRail
              items={watchPartyRailItems}
              onJoin={handleJoinWatchParty}
              title="Live Watch Parties"
              className="mt-0"
            />
            {/* Optional: keep a “View all” entry point as a separate tiny link if you want */}
            {/* <button
              type="button"
              onClick={handleOpenWatchParties}
              className="mt-2 text-[11px] tracking-[0.22em] uppercase text-cyan-300/70 hover:text-cyan-200 transition"
            >
              View all
            </button> */}
          </div>
        </div>

        {/* Main grid */}
        <div className={ui.grid}>
          {/* LEFT SIDEBAR */}
          <aside className={`hidden lg:block ${ui.col}`}>
            <HotThreadsPanel
              threads={posts}
              onOpenThread={goToThread}
              onLike={handleLike}
              onReply={handleReply}
              likedPosts={likedPosts}
            />
          </aside>

          {/* MAIN FEED */}
          <section className={`${ui.col} flex flex-col min-h-0`}>
            <CommunityStartThreadBar
              user={user}
              activeTopicTag="All topics"
              onClickPost={onClickPost}
            />

            {/* Invisible scroll container (no padding/bg/border). Scrollbar hidden. */}
            <div className={ui.feedScrollOuter}>
  <div className={ui.feedScrollInner}>
    <div className={ui.feed}>
      {postsError && <div className={ui.errorText}>{postsError}</div>}

      {loadingPosts ? (
        <div className={ui.infoText}>Loading threads…</div>
      ) : filteredPosts.length === 0 ? (
        <div className={ui.infoText}>{q ? "No results found." : "No threads yet."}</div>
      ) : (
        filteredPosts.map((post) => {
          const cardPost = forumFeedRowToFeedPostCard(post, likedPosts.has(post.id));
          const isOwner =
            Boolean(user) &&
            post.authorId != null &&
            viewerIdNum != null &&
            Number(post.authorId) === Number(viewerIdNum);
          return (
            <FeedPostCard
              key={post.id}
              post={cardPost}
              viewerId={viewerIdNum}
              accent="emerald"
              clubSlug={post.clubSlug || post.club_slug || ""}
              club={null}
              membership={null}
              canInteract={Boolean(user)}
              canDeleteOverride={isOwner}
              shareFeedbackId={shareFeedback?.postId}
              shareFeedbackText={shareFeedback?.text}
              replyNavState={{}}
              likeNotInteractTitle="Log in to like"
              deleteConfirmMessage="Are you sure you want to delete this post?"
              onOpenThread={() => goToThread(post)}
              onLikeClick={async () => {
                await handleLike(post);
              }}
              onShareClick={async () => {
                await handleFeedShare(post);
              }}
              onDeleteClick={async () => {
                await handleDelete(post);
              }}
              onEditPost={isOwner ? () => handleEdit(post) : undefined}
            />
          );
        })
      )}
    </div>
  </div>
</div>


            {/* Mobile floating "New Thread" CTA */}
            <div className={ui.mobileCtaWrap}>
              <div className={ui.mobileCtaInner}>
                <div className="mx-auto max-w-[640px]">
                  <button onClick={onClickPost} className={ui.mobileCtaBtn}>
                    New Thread
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* RIGHT SIDEBAR */}
          <aside className={`hidden lg:block ${ui.col}`}>
            <CommunityClubsBar clubs={clubs} title="Clubs" subtitle="DISCOVER" />
          </aside>
        </div>

        <UnifiedPostComposer
          isOpen={isComposerOpen}
          onClose={() => setIsComposerOpen(false)}
          surface="community"
          club={null}
          membership={null}
          clubPickerOptions={clubPickerOptions}
          headlineTitle="Create post"
          onSuccess={handleUnifiedComposerSuccess}
        />
      </div>
    </div>
  );
}

/* ---------- helpers ---------- */

/**
 * Parse `forum_post_attachments` JSON from GET /api/forum into club-style rows
 * (`{ kind: "image", url, sort_order }`) so `FeedPostCard` / `getImageAttachmentsFromPost`
 * receive the same multi-image shape as club feed posts (carousel arrows when n > 1).
 */
function forumClubStyleImageAttachmentsFromPostJson(attachmentsRaw) {
  let arr = attachmentsRaw;
  if (arr == null) return [];
  if (typeof arr === "string") {
    try {
      arr = JSON.parse(arr);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(arr) || arr.length === 0) return [];

  const rows = arr
    .filter((a) => a && String(a.kind || "").toLowerCase() === "image" && String(a.url || "").trim())
    .map((a, idx) => ({
      kind: "image",
      url: String(a.url).trim(),
      sort_order: Number(a.sort_order ?? a.sortOrder) || idx,
      ...(a.id != null && String(a.id).trim() !== "" && Number.isFinite(Number(a.id))
        ? { id: Number(a.id) }
        : {}),
    }));

  rows.sort((a, b) => {
    if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
    const ia = Number(a.id);
    const ib = Number(b.id);
    if (Number.isFinite(ia) && Number.isFinite(ib)) return ia - ib;
    return 0;
  });

  return rows;
}

function normalizeForumRowToThread(post) {
  const fmt = String(post.content_format || "").trim();
  const titleStr = String(post.title || "").trim();
  const contentStr = String(post.content || "").trim();
  let displayTitle = post.title || "";
  let displayBody = post.content || "";
  if (fmt === "club_title_body_v1") {
    displayTitle = titleStr;
    displayBody = contentStr && contentStr !== titleStr ? contentStr : "";
  }

  const clubIdRaw = post.club_id ?? post.clubId;
  const clubIdNum = Number(clubIdRaw);
  const clubSlugStr =
    post.club_slug != null && String(post.club_slug).trim() !== ""
      ? String(post.club_slug).trim()
      : "";

  const normalized = {
    id: String(post.post_id ?? post.id),
    author: post.username || "Guest",
    authorId: post.user_id,
    avatar: post.profile_picture || null,
    createdAt: post.created_at || new Date().toISOString(),
    displayTime: post.created_at ? new Date(post.created_at).toLocaleString() : "—",
    title: displayTitle,
    body: displayBody,
    likes: Number.isFinite(post.likes) ? post.likes : parseInt(post.likes || 0, 10),
    replies: Number.isFinite(post.replies) ? post.replies : parseInt(post.replies || 0, 10),
    rating: post.rating || null,
    topicId: post.topic || post.topic_id || post.topicId || null,
    tags: normalizeForumTagsForThread(post.tags),
    ...(Number.isFinite(clubIdNum) && clubIdNum > 0 ? { clubId: clubIdNum, club_id: clubIdNum } : {}),
    ...(clubSlugStr ? { clubSlug: clubSlugStr, club_slug: clubSlugStr } : {}),
    ...(post.club_name != null && String(post.club_name).trim() !== ""
      ? { club_name: String(post.club_name).trim() }
      : {}),
    ...(post.club_avatar_image_url != null && String(post.club_avatar_image_url).trim() !== ""
      ? { club_avatar_image_url: String(post.club_avatar_image_url).trim() }
      : {}),
  };

  if (post.attachment_type) {
    const attachmentData = {};

    if (post.attachment_type === "anime" && post.attachment_anime_id) {
      attachmentData.id = post.attachment_anime_id;
      attachmentData.title = post.anime_title || "Anime";
      attachmentData.image_url = post.anime_image_url || "";
      attachmentData.episodes = post.anime_episodes || null;
    } else if (post.attachment_type === "artwork" && post.attachment_artwork_id) {
      attachmentData.id = post.attachment_artwork_id;
      attachmentData.title = post.artwork_title || "Artwork";
      attachmentData.image_url = post.artwork_image_url || "";
    } else if (post.attachment_type === "image" && post.attachment_image_url) {
      attachmentData.url = post.attachment_image_url;
      attachmentData.preview = post.attachment_image_url;
    }

    normalized.attachment = {
      type: post.attachment_type,
      data: attachmentData,
    };
  }

  // Legacy column-only image (no attachment_type), common on some forum_posts rows.
  if (!normalized.attachment && post.attachment_image_url) {
    const u = String(post.attachment_image_url).trim();
    if (u) {
      normalized.attachment = {
        type: "image",
        data: { url: u, preview: u },
      };
    }
  }

  const multiImages = forumClubStyleImageAttachmentsFromPostJson(post.attachments);
  if (multiImages.length > 0) {
    normalized.attachments = multiImages;
  }

  // If API includes forum_post_attachments but legacy attachment fields were empty,
  // keep a single `attachment` for older UI (first image) while `attachments` drives the feed carousel.
  if (!normalized.attachment && multiImages.length > 0) {
    const u = multiImages[0].url;
    normalized.attachment = {
      type: "image",
      data: { url: u, preview: u },
    };
  }

  return normalized;
}

function getDisplayName(user) {
  if (!user) return "Guest";
  return user.username || user.name || "User";
}

function safeArr(x) {
  return Array.isArray(x) ? x : [];
}

function daysSince(iso) {
  const t = Date.parse(iso || "");
  if (!Number.isFinite(t)) return 365;
  const diff = Date.now() - t;
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

function scoreTrending(p) {
  const likes = Number(p.likes) || 0;
  const replies = Number(p.replies) || 0;
  const recencyBoost = Math.max(0, 30 - daysSince(p.createdAt));
  return likes * 2 + replies * 3 + recencyBoost;
}
